/**
 * Shared HTML table design tokens for all native Power BI reports.
 *
 * Change values here to update table look across reports.
 * Prefer importing from this file instead of hardcoding colors/fonts/sizes.
 *
 * Usage:
 *   import { TABLE_FONT, TH_BG, TH_TEXT, CELL_FG, EVEN_BG, ODD_BG } from "../reportTableConfig";
 *   // or
 *   import { REPORT_TABLE, thHeaderStyle, tdCellStyle } from "../reportTableConfig";
 */

import { REPORT_FONT } from "./reportFonts";
import { AD_MIST } from "./reportColorPalette";

/* ══════════════════════════════════════════════════════════════ */
/*  Nested config object                                          */
/* ══════════════════════════════════════════════════════════════ */

export const REPORT_TABLE = {
  /** CSS font-family for JS style objects / makeStyles */
  font: REPORT_FONT,
  /** Unquoted stack for HTML style="font-family:…" attributes */
  fontCss: "Roboto, Helvetica, Arial, sans-serif",

  header: {
    /** Primary column header background */
    bg: "#002554",
    /** Group / band header (row-1) background */
    bgBand: "#002554",
    /** Header label text */
    text: "#d5dae0",
    /** Softer header text */
    textMuted: "rgba(255,255,255,0.75)",
    /** Stronger header text */
    textStrong: "rgba(255,255,255,0.9)",
    /** Header cell border / inset divider */
    border: "#4a4a4c",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    height: 34,
    /**
     * Actuals month headers (2nd header row) — e.g. CM Actuals vs Forecast.
     * Brand mist from reportColorPalette.
     */
    actualBg: AD_MIST,
    actualText: "#000000",
  },

  cell: {
    text: "#334155",
    textStrong: "#111827",
    textMuted: "#6B7280",
    evenBg: "#FFFFFF",
    oddBg: "#F7FAFC",
    totalBg: "#F0FFF4",
    totalBgAlt: "#E6FFF1",
    rowTotalBg: "#F8FAFC",
    fontSize: 12,
    height: 36,
  },

  colors: {
    negative: "#DC2626",
    positive: "#10B981",
    accent: "#4F46E5",
    border: "#E2E8F0",
    borderStrong: "#CBD5E1",
    hairline: "#E5E7EB",
  },

  sticky: {
    headerZ: 6,
    stickyZ: 4,
  },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Flat exports (drop-in for local const TH_BG / FONT / …)        */
/* ══════════════════════════════════════════════════════════════ */

export const TABLE_FONT = REPORT_TABLE.font;
export const TABLE_FONT_CSS = REPORT_TABLE.fontCss;

export const TH_BG = REPORT_TABLE.header.bg;
export const TH_BG_BAND = REPORT_TABLE.header.bgBand;
export const TH_TEXT = REPORT_TABLE.header.text;
export const TH_TEXT_MUTED = REPORT_TABLE.header.textMuted;
export const TH_TEXT_STRONG = REPORT_TABLE.header.textStrong;
export const TH_BORDER = REPORT_TABLE.header.border;
export const TH_FONT_SIZE = REPORT_TABLE.header.fontSize;
export const TH_HEIGHT = REPORT_TABLE.header.height;
/** Actuals month header (2nd row) background / text */
export const TH_ACTUAL_BG = REPORT_TABLE.header.actualBg;
export const TH_ACTUAL_TEXT = REPORT_TABLE.header.actualText;

/** Inset grid lines on header cells */
export const TH_SHADOW = `inset -1px 0 0 ${TH_BORDER}, inset 0 -1px 0 ${TH_BORDER}`;
export const TH_SHADOW_ROW1 = `inset -1px 0 0 ${TH_BORDER}`;
export const TH_SHADOW_BOTTOM = `inset 0 -2px 0 rgba(255,255,255,.22)`;

export const CELL_FG = REPORT_TABLE.cell.text;
export const CELL_FG_STRONG = REPORT_TABLE.cell.textStrong;
export const CELL_FG_MUTED = REPORT_TABLE.cell.textMuted;
export const EVEN_BG = REPORT_TABLE.cell.evenBg;
export const ODD_BG = REPORT_TABLE.cell.oddBg;
export const TOTAL_BG = REPORT_TABLE.cell.totalBg;
export const TOTAL_BG_ALT = REPORT_TABLE.cell.totalBgAlt;
export const ROW_TOTAL_BG = REPORT_TABLE.cell.rowTotalBg;
export const TD_FONT_SIZE = REPORT_TABLE.cell.fontSize;
export const ROW_HEIGHT = REPORT_TABLE.cell.height;

export const NEG_COLOR = REPORT_TABLE.colors.negative;
export const POS_COLOR = REPORT_TABLE.colors.positive;
export const ACCENT_COLOR = REPORT_TABLE.colors.accent;
export const TABLE_BORDER = REPORT_TABLE.colors.border;
export const TABLE_BORDER_STRONG = REPORT_TABLE.colors.borderStrong;
export const TABLE_HAIRLINE = REPORT_TABLE.colors.hairline;

export const Z_HEAD = REPORT_TABLE.sticky.headerZ;
export const Z_STICKY = REPORT_TABLE.sticky.stickyZ;

/* ══════════════════════════════════════════════════════════════ */
/*  Style helpers                                                 */
/* ══════════════════════════════════════════════════════════════ */

/** Base <th> look for dark report headers */
export function thHeaderStyle(overrides = {}) {
  return {
    background: TH_BG,
    color: TH_TEXT,
    fontFamily: TABLE_FONT,
    fontSize: TH_FONT_SIZE,
    fontWeight: REPORT_TABLE.header.fontWeight,
    letterSpacing: REPORT_TABLE.header.letterSpacing,
    textTransform: REPORT_TABLE.header.textTransform,
    border: "none",
    whiteSpace: "nowrap",
    boxShadow: TH_SHADOW_BOTTOM,
    ...overrides,
  };
}

/** Base <td> look; pass { alt: true } for odd-row stripe, { num: true } for right-align */
export function tdCellStyle({ alt = false, num = false, total = false } = {}, overrides = {}) {
  return {
    fontFamily: TABLE_FONT,
    fontSize: TD_FONT_SIZE,
    color: total ? CELL_FG_STRONG : CELL_FG,
    background: total ? TOTAL_BG : alt ? ODD_BG : EVEN_BG,
    textAlign: num ? "right" : "left",
    borderBottom: `1px solid ${TABLE_BORDER}`,
    whiteSpace: "nowrap",
    ...overrides,
  };
}

/** Stripe background for row index i (0-based) */
export function rowStripeBg(i) {
  return i % 2 === 0 ? EVEN_BG : ODD_BG;
}

/** Vertical + horizontal scroll for paginated report tables (sticky header/total). */
export const TABLE_SCROLL_WRAP_STYLE = {
  overflow: "auto",
  maxHeight: "calc(100vh - 320px)",
  background: "#FFFFFF",
};
